/**
 * Chat Message Validator
 * Validates message content before sending
 */

// Maximum message content length (10KB)
const MAX_MESSAGE_LENGTH = 10 * 1024;

// Allowed content types
const ALLOWED_CONTENT_TYPES = ['text', 'image', 'file', 'system'] as const;
type ContentType = typeof ALLOWED_CONTENT_TYPES[number];

// Characters that should be stripped from messages (control chars except newlines/tabs)
const CONTROL_CHAR_REGEX = /[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g;

// XSS-dangerous patterns (basic protection)
const XSS_PATTERNS = [
  /<script\b[^>]*>/gi,
  /javascript:/gi,
  /on\w+\s*=/gi,
  /<iframe\b/gi,
  /<embed\b/gi,
  /<object\b/gi,
];

export interface ValidationResult {
  valid: boolean;
  sanitizedContent?: string;
  error?: string;
}

/**
 * Validate and sanitize message content
 */
export function validateMessageContent(
  content: string,
  contentType: string = 'text'
): ValidationResult {
  // Check content type
  if (!ALLOWED_CONTENT_TYPES.includes(contentType as ContentType)) {
    return {
      valid: false,
      error: `Неподдерживаемый тип контента: ${contentType}`,
    };
  }

  // Check for empty content
  if (!content || typeof content !== 'string') {
    return {
      valid: false,
      error: 'Сообщение не может быть пустым',
    };
  }

  // Trim whitespace
  let sanitized = content.trim();

  // Check if empty after trim
  if (sanitized.length === 0) {
    return {
      valid: false,
      error: 'Сообщение не может быть пустым',
    };
  }

  // Check length
  if (sanitized.length > MAX_MESSAGE_LENGTH) {
    return {
      valid: false,
      error: `Сообщение слишком длинное. Максимум ${MAX_MESSAGE_LENGTH} символов`,
    };
  }

  // For text messages, apply sanitization
  if (contentType === 'text') {
    // Remove control characters
    sanitized = sanitized.replace(CONTROL_CHAR_REGEX, '');

    // Check for XSS patterns - BLOCK dangerous content
    for (const pattern of XSS_PATTERNS) {
      if (pattern.test(sanitized)) {
        // Reset regex lastIndex
        pattern.lastIndex = 0;
        console.warn('[Chat] Blocked dangerous content pattern');
        return {
          valid: false,
          error: 'Сообщение содержит недопустимый контент',
        };
      }
    }

    // HTML entity escaping to prevent XSS on display
    sanitized = sanitized
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#x27;');

    // Normalize multiple newlines to max 3
    sanitized = sanitized.replace(/\n{4,}/g, '\n\n\n');

    // Normalize multiple spaces to single space (except at line start for code)
    sanitized = sanitized.replace(/  +/g, ' ');
  }

  // For image/file types, validate it looks like a URL
  if (contentType === 'image' || contentType === 'file') {
    const urlPattern = /^https?:\/\//i;
    if (!urlPattern.test(sanitized)) {
      return {
        valid: false,
        error: 'Неверный URL для файла',
      };
    }
  }

  return {
    valid: true,
    sanitizedContent: sanitized,
  };
}

/**
 * Validate room ID format
 */
export function validateRoomId(roomId: string): boolean {
  if (!roomId || typeof roomId !== 'string') {
    return false;
  }
  // UUID v4 pattern
  const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  return uuidPattern.test(roomId);
}

/**
 * Check if content might contain PII (for logging purposes)
 */
export function mightContainPII(content: string): boolean {
  const emailPattern = /[\w.+-]+@[\w.-]+/i;
  const cardPattern = /\d{4}[\s-]?\d{4}[\s-]?\d{4}[\s-]?\d{4}/;
  
  // Remove date patterns before checking for phone numbers
  const datePattern = /\b\d{4}-\d{2}-\d{2}\b/g;
  const contentWithoutDates = content.replace(datePattern, '');
  const phonePattern = /\+?\d[\d\s().-]{6,}\d/;
  
  return (
    emailPattern.test(content) ||
    phonePattern.test(contentWithoutDates) ||
    cardPattern.test(content)
  );
}
